import { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { dashboardSections } from '@/app/dashboard/navigation';
import { accentTextClass } from '@/app/dashboard/accent';

/**
 * Below 768px, the sidebar is replaced entirely by a bottom tab bar with
 * 5 slots (4 primary sections + "More"). docs/DESIGN_SYSTEM.md §4.12.
 * Respects safe-area-inset-bottom for gesture-nav Android devices.
 */
export function MobileNav() {
  const { guildId } = useParams<{ guildId: string }>();
  const [moreOpen, setMoreOpen] = useState(false);

  const primary = dashboardSections.filter((s) => s.primaryMobile);
  const rest = dashboardSections.filter((s) => !s.primaryMobile);

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-bg-elevated border-t border-border-subtle flex"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Dashboard sections"
      >
        {primary.map((section) => {
          const to = `/dashboard/${guildId}${section.path ? `/${section.path}` : ''}`;
          return (
            <NavLink
              key={section.path}
              to={to}
              end={section.path === ''}
              className={({ isActive }) =>
                [
                  'flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[44px] text-micro',
                  isActive ? accentTextClass[section.accent] : 'text-text-secondary',
                ].join(' ')
              }
            >
              <section.icon size={20} aria-hidden="true" />
              {section.label}
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[44px] text-micro text-text-secondary"
          aria-haspopup="true"
          aria-expanded={moreOpen}
        >
          <Menu size={20} aria-hidden="true" />
          More
        </button>
      </nav>

      {moreOpen ? (
        <div className="md:hidden fixed inset-0 z-40 bg-bg-elevated flex flex-col">
          <div className="h-14 flex items-center justify-between px-4 border-b border-border-subtle">
            <span className="text-h3 text-text-primary">More sections</span>
            <button
              type="button"
              onClick={() => setMoreOpen(false)}
              className="h-11 w-11 flex items-center justify-center text-text-secondary"
              aria-label="Close menu"
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {rest.map((section) => {
              const to = `/dashboard/${guildId}${section.path ? `/${section.path}` : ''}`;
              return (
                <NavLink
                  key={section.path}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-sm text-body-medium text-text-primary hover:bg-bg-surface"
                >
                  <section.icon size={20} aria-hidden="true" />
                  {section.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}
